// AgentForge V1's own WebSocket connection.
//
// Same URL legacy uses (`wss://${host}/agentforge/api/v1/ws?token=...`)
// but a separate socket — V1 is self-contained and doesn't share the
// singleton from `useMissionControlWS`. Mount this exactly once
// (in the `AgentForge` umbrella) so each WS frame is processed a
// single time.
//
// Reconnect strategy — four layers, defense in depth:
//   1. last_event_id replay (server XRANGEs the durable Redis stream from
//      the last frame the client saw, so events emitted during the
//      disconnect window aren't lost).
//   2. App-level heartbeat: server sends a `server_ping` JSON frame every
//      ~25s; if no frame at all arrives for 50s, we force-close and
//      reconnect immediately. Catches TCP half-open (LB kill, NAT
//      timeout, server crash that left the socket dangling).
//   3. JWT refresh on close-before-open: if a reconnect attempt closes
//      without ever firing `onopen`, the cached `apiv1Auth` is the most
//      likely cause — we refresh once before retrying so we don't loop
//      forever on a dead token.
//   4. Exponential backoff (1s → 30s) for unrelated server failures.
//
// Subscribers receive raw `{type, payload}` frames; `server_ping` is
// filtered out at this layer because it carries no UI signal.

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRefreshToken } from '@/lib/auth-interceptor';

// Wire format from AgentForge WS: { type: "<event_type>", payload: {...} }
// Server may attach an `id` to events that come from the durable Redis
// Streams log; we keep it as the resume cursor for the next reconnect.
export interface AgentForgeWSMessage {
  type: string;
  id?: string;
  payload: Record<string, any>;
}

type MessageHandler = (_msg: AgentForgeWSMessage) => void;

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
// If no frame (event OR server_ping) arrives within this window the
// socket is considered dead and we force-reconnect. server_ping ticks
// every ~25s, so 50s = two missed pings = real liveness failure rather
// than a transient hiccup.
const STALE_FRAME_THRESHOLD_MS = 50_000;
const STALE_CHECK_INTERVAL_MS = 5_000;

const buildWsUrl = (token: string, lastEventId: string | null): string => {
  const agentforgeUrl = import.meta.env.VITE_AGENTFORGE_URL;
  const host = agentforgeUrl
    ? new URL(agentforgeUrl).host
    : window.location.host;
  const proto = agentforgeUrl
    ? agentforgeUrl.startsWith('https')
      ? 'wss'
      : 'ws'
    : window.location.protocol === 'https:'
      ? 'wss'
      : 'ws';
  let url = `${proto}://${host}/agentforge/api/v1/ws?token=${token}`;
  if (lastEventId) {
    url += `&last_event_id=${encodeURIComponent(lastEventId)}`;
  }
  return url;
};

export interface UseAgentForgeWSResult {
  connected: boolean;
  reconnecting: boolean;
  /** Monotonically incremented every time the socket completes a
   * reconnect (i.e. `onopen` fires after a prior `onopen` happened in
   * this hook's lifetime). Consumers can watch this to trigger a
   * session-level refetch — "we lost frames during the gap, sync up
   * via HTTP." Stays at 0 for the very first connect. */
  reconnectGeneration: number;
  subscribe: (_handler: MessageHandler) => () => void;
  /** Send a JSON frame to the server. Returns false if the socket is closed. */
  send: (_data: unknown) => boolean;
}

export const useAgentForgeWS = (): UseAgentForgeWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const handlersRef = useRef<Set<MessageHandler>>(new Set());
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const mountedRef = useRef(true);
  const wasConnectedRef = useRef(false);
  const connectRef = useRef<() => void>();
  const lastEventIdRef = useRef<string | null>(null);
  // 0 until the first `onopen` stamps it. The stale-frame watchdog only
  // reads this once readyState === OPEN, by which point onopen has run.
  const lastFrameAtRef = useRef<number>(0);
  // Tracks the current attempt's "did we ever see onopen". Reset on
  // every `connect()` call. If we close without ever seeing onopen the
  // token is probably stale → refresh before retrying.
  const sawOpenThisAttemptRef = useRef(false);
  // Has refreshToken already been tried for the current bad-token
  // failure cluster? Reset on a successful open.
  const refreshTriedRef = useRef(false);

  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectGeneration, setReconnectGeneration] = useState(0);

  const subscribe = useCallback((handler: MessageHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  // Send a JSON frame to the server (client→server). Returns false if the socket
  // isn't open (caller decides whether to retry). Used to push a refreshed Kula
  // token — all token communication stays on the WS.
  const send = useCallback((data: unknown): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    } catch {
      return false;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    const delay = reconnectDelayRef.current;
    reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
    reconnectTimeoutRef.current = setTimeout(
      () => connectRef.current?.(),
      delay
    );
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem('apiv1Auth');
    if (!token) {
      console.warn(
        '[agentforge] no `apiv1Auth` token in localStorage at this origin — WS will not connect. Log in on this origin first.'
      );
      return;
    }

    sawOpenThisAttemptRef.current = false;
    const url = buildWsUrl(token, lastEventIdRef.current);
    console.info(
      '[agentforge] connecting WS:',
      url.replace(/token=[^&]+/, 'token=…')
    );
    const ws = new WebSocket(url);

    ws.onopen = () => {
      sawOpenThisAttemptRef.current = true;
      refreshTriedRef.current = false;
      lastFrameAtRef.current = Date.now();
      const isReconnect = wasConnectedRef.current;
      wasConnectedRef.current = true;
      setConnected(true);
      setReconnecting(false);
      reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      if (isReconnect) {
        // Tell consumers to backfill via HTTP — even with stream replay
        // we may have lost the tail past the replay cap, and a full
        // session refetch is cheap and correct.
        setReconnectGeneration((g) => g + 1);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (wasConnectedRef.current) setReconnecting(true);
      wsRef.current = null;
      if (!mountedRef.current) return;

      // Close-without-open is the canonical "token rejected" pattern.
      // Try one refresh before falling into the exponential backoff
      // loop — if the refresh succeeds the next attempt picks up the
      // fresh token from localStorage.
      if (!sawOpenThisAttemptRef.current && !refreshTriedRef.current) {
        refreshTriedRef.current = true;
        fetchRefreshToken().finally(() => {
          // Connect immediately after refresh resolves (success OR
          // failure); the backoff still kicks in on the *next* close
          // if we're truly hosed.
          reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
          reconnectTimeoutRef.current = setTimeout(
            () => connectRef.current?.(),
            0
          );
        });
        return;
      }

      scheduleReconnect();
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      lastFrameAtRef.current = Date.now();
      try {
        const msg = JSON.parse(event.data) as AgentForgeWSMessage;
        // Track resume cursor for the next reconnect. Only emitter
        // events carry ids; legacy hub-broadcast frames (trust events,
        // etc.) don't. server_ping has none either — it's pure
        // liveness signal.
        if (msg.id) lastEventIdRef.current = msg.id;
        if (msg.type === 'server_ping') return;
        handlersRef.current.forEach((handler) => handler(msg));
      } catch {
        // Ignore malformed frames.
      }
    };

    wsRef.current = ws;
  }, [scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Foreground reconnect — when the user returns to the tab and the
  // socket is dead, don't wait out the backoff.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connect]);

  // Stale-frame watchdog. The protocol-level WS ping/pong is invisible
  // to browser JS, so an idle session with a half-dead socket would
  // never trip `onclose`. We rely on the server's app-level
  // `server_ping` heartbeat: if it stops arriving, the socket is dead.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const age = Date.now() - lastFrameAtRef.current;
      if (age > STALE_FRAME_THRESHOLD_MS) {
        console.warn(
          `[agentforge] no WS frames for ${Math.round(age / 1000)}s — forcing reconnect`
        );
        try {
          wsRef.current.close();
        } catch {
          // already closed
        }
      }
    }, STALE_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return { connected, reconnecting, reconnectGeneration, subscribe, send };
};
